import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type {
	AiManagedObjectConfig,
	AiManagedObjectConfigCreateInput,
	AiManagedObjectDesiredUpdateFrequency,
	AiManagedObjectDesiredUpdateFrequencyInput,
	AiManagedObjectSourceInput,
} from '@atlassian/ai-managed-objects/types';
import {
	getCurrentPageSource,
	getResolvedSourcesFromEntries,
	getSourceEntriesFromSources,
	getSourceUrlsFromEntries,
	getSourcesFromText,
} from '@atlassian/ai-managed-objects/sources';

import type {
	AgentManagedExtensionTemplateConfig,
	ManagedBlockSourceMode,
} from '../../../common/agentManagedExtensionPluginType';

import { DEFAULT_GOAL, isGoal } from './goals';
import { DEFAULT_OUTPUT_FORMAT, getOutputFormatForGoal } from './output-formats';
import type { ManagedBlockConfiguration, ManagedBlockUpdateInterval } from './types';

export const DEFAULT_UPDATE_INTERVAL: ManagedBlockUpdateInterval = 'long';
export const DEFAULT_SOURCE_MODE: ManagedBlockSourceMode = 'current-page';
export const MAX_ADDITIONAL_CONTEXT_LENGTH = 500;

const UPDATE_INTERVAL_DYNAMIC_CONFIG = 'ai-managed-blocks-update-interval';

const UPDATE_INTERVAL_FALLBACK_VALUES: Record<ManagedBlockUpdateInterval, string> = {
	short: 'PT1H',
	medium: 'PT7H',
	long: 'P1D',
};

type UpdateIntervalValues = Record<ManagedBlockUpdateInterval, string>;

const getUpdateIntervalValues = (): UpdateIntervalValues => {
	const dynamicConfig = FeatureGates.getExperiment(UPDATE_INTERVAL_DYNAMIC_CONFIG, {
		fireExperimentExposure: false,
	}).value as { value?: Partial<Record<ManagedBlockUpdateInterval, string>> };
	const config = dynamicConfig.value ?? {};

	return {
		short: config.short ?? UPDATE_INTERVAL_FALLBACK_VALUES.short,
		medium: config.medium ?? UPDATE_INTERVAL_FALLBACK_VALUES.medium,
		long: config.long ?? UPDATE_INTERVAL_FALLBACK_VALUES.long,
	};
};

export const getDesiredUpdateFrequencyForInterval = (
	updateInterval?: ManagedBlockUpdateInterval,
): AiManagedObjectDesiredUpdateFrequencyInput | undefined => {
	const value = updateInterval ? getUpdateIntervalValues()[updateInterval] : undefined;

	return value ? { type: 'INTERVAL', value } : undefined;
};

export const getUpdateIntervalFromDesiredFrequency = (
	frequency?: AiManagedObjectDesiredUpdateFrequency | null,
): ManagedBlockUpdateInterval | undefined => {
	if (!frequency) {
		return undefined;
	}

	if (frequency.type !== 'INTERVAL') {
		return DEFAULT_UPDATE_INTERVAL;
	}

	const updateInterval = Object.entries(getUpdateIntervalValues()).find(
		([, value]) => value === frequency.value,
	)?.[0] as ManagedBlockUpdateInterval | undefined;

	return updateInterval ?? DEFAULT_UPDATE_INTERVAL;
};

export const getDefaultPanelConfiguration = (): ManagedBlockConfiguration => ({
	additionalContext: '',
	goal: DEFAULT_GOAL,
	isAdditionalContextSelected: false,
	mode: 'configuration',
	outputFormat: DEFAULT_OUTPUT_FORMAT,
	prompt: '',
	sourceEntries: [],
	sourceMode: DEFAULT_SOURCE_MODE,
	sourceUrls: '',
	updateInterval: DEFAULT_UPDATE_INTERVAL,
});

export const getDefaultStructuredPanelConfiguration = (
	previous: ManagedBlockConfiguration,
): ManagedBlockConfiguration => ({
	...previous,
	additionalContext: '',
	goal: DEFAULT_GOAL,
	isAdditionalContextSelected: false,
	mode: 'configuration',
	outputFormat: DEFAULT_OUTPUT_FORMAT,
	sourceEntries: [],
	sourceMode: DEFAULT_SOURCE_MODE,
	sourceUrls: '',
});

export const getPanelConfigurationFromTemplateConfig = (
	templateConfig?: AgentManagedExtensionTemplateConfig,
): ManagedBlockConfiguration => {
	const defaultConfiguration = getDefaultPanelConfiguration();

	if (!templateConfig) {
		return defaultConfiguration;
	}

	return {
		...defaultConfiguration,
		goal: templateConfig.goal,
		outputFormat: getOutputFormatForGoal(templateConfig.goal, templateConfig.outputFormat),
		sourceMode: templateConfig.sourceMode,
	};
};

export const getPanelConfigurationFromAimoConfig = (
	config?: AiManagedObjectConfig | null,
): ManagedBlockConfiguration => {
	if (!config) {
		return getDefaultPanelConfiguration();
	}

	const goal = isGoal(config.goal) ? config.goal : DEFAULT_GOAL;
	const outputFormat = getOutputFormatForGoal(goal, config.goalOperations?.outputFormat);
	const sourceEntries = getSourceEntriesFromSources(config.sources);
	const sourceUrls = getSourceUrlsFromEntries(sourceEntries);
	const additionalContext =
		typeof config.goalOperations?.additionalContext === 'string'
			? config.goalOperations.additionalContext.slice(0, MAX_ADDITIONAL_CONTEXT_LENGTH)
			: '';

	return {
		additionalContext,
		goal,
		isAdditionalContextSelected: additionalContext.trim().length > 0,
		mode: config.goal ? 'configuration' : 'prompt',
		outputFormat,
		prompt: config.prompt ?? '',
		sourceEntries,
		sourceMode: sourceEntries.length > 0 ? 'manual' : DEFAULT_SOURCE_MODE,
		sourceUrls,
		updateInterval: getUpdateIntervalFromDesiredFrequency(config.desiredUpdateFrequency),
	};
};

export const toAiManagedObjectConfigInput = (
	configuration: ManagedBlockConfiguration,
	currentPageSourceUrl?: string,
): AiManagedObjectConfigCreateInput => {
	const desiredUpdateFrequency = getDesiredUpdateFrequencyForInterval(configuration.updateInterval);

	if (configuration.mode === 'prompt') {
		const resolvedSources = getResolvedSourcesFromEntries(configuration.sourceEntries);

		return {
			desiredUpdateFrequency,
			prompt: configuration.prompt.trim(),
			...(resolvedSources.length > 0 ? { sources: resolvedSources } : {}),
		};
	}

	const goal = configuration.goal;
	const outputFormat = getOutputFormatForGoal(goal, configuration.outputFormat);
	const additionalContext = configuration.isAdditionalContextSelected
		? configuration.additionalContext.trim().slice(0, MAX_ADDITIONAL_CONTEXT_LENGTH)
		: '';
	const resolvedSources = getResolvedSourcesFromEntries(configuration.sourceEntries);
	const sources =
		configuration.sourceMode === 'current-page'
			? getCurrentPageSource(currentPageSourceUrl)
			: resolvedSources.length > 0
				? resolvedSources
				: getSourcesFromText(configuration.sourceUrls);

	return {
		desiredUpdateFrequency,
		goal,
		goalOperations: {
			...(additionalContext ? { additionalContext } : {}),
			outputFormat,
		},
		prompt: null,
		sources,
	};
};

export const toAiManagedObjectConfigInputWithUpdateInterval = (
	config: AiManagedObjectConfig,
	updateInterval?: ManagedBlockUpdateInterval,
): AiManagedObjectConfigCreateInput => ({
	desiredUpdateFrequency: getDesiredUpdateFrequencyForInterval(updateInterval),
	goal: config.goal ?? undefined,
	goalOperations: config.goalOperations ?? undefined,
	prompt: config.prompt ?? null,
	sources:
		config.sources?.map((source) => ({
			app: source.app,
			...(source.url ? { url: source.url } : {}),
		})) ?? undefined,
});

type ComparableConfig = {
	desiredUpdateFrequency?: AiManagedObjectDesiredUpdateFrequencyInput;
	goal?: string;
	goalOperations?: Record<string, unknown>;
	prompt: string | null;
	sources?: AiManagedObjectSourceInput[];
};

const normalizeDesiredUpdateFrequency = (
	config?: AiManagedObjectConfigCreateInput | AiManagedObjectConfig | null,
): AiManagedObjectDesiredUpdateFrequencyInput | undefined => {
	if (!config) {
		return getDesiredUpdateFrequencyForInterval(DEFAULT_UPDATE_INTERVAL);
	}

	return config.desiredUpdateFrequency ?? undefined;
};

const normalizeConfig = (
	config?: AiManagedObjectConfigCreateInput | AiManagedObjectConfig | null,
): ComparableConfig => ({
	desiredUpdateFrequency: normalizeDesiredUpdateFrequency(config),
	goal: config?.goal ?? undefined,
	goalOperations: config?.goalOperations ?? undefined,
	prompt: config?.prompt ?? '',
	sources:
		config?.sources?.map((source) => ({
			app: source.app,
			...(source.url ? { url: source.url } : {}),
		})) ?? undefined,
});

const sortConfigValue = (value: unknown): unknown => {
	if (Array.isArray(value)) {
		return value.map(sortConfigValue);
	}

	if (!value || typeof value !== 'object') {
		return value;
	}

	return Object.fromEntries(
		Object.entries(value)
			.filter(([, entryValue]) => entryValue !== undefined)
			.sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
			.map(([key, entryValue]) => [key, sortConfigValue(entryValue)]),
	);
};

const stableStringify = (value: unknown): string => JSON.stringify(sortConfigValue(value));

const stringifyConfig = (
	config?: AiManagedObjectConfigCreateInput | AiManagedObjectConfig | null,
): string => stableStringify(normalizeConfig(config));

export const arePanelConfigurationsEqual = (
	left: ManagedBlockConfiguration,
	right: ManagedBlockConfiguration,
	currentPageSourceUrl?: string,
): boolean =>
	stringifyConfig(toAiManagedObjectConfigInput(left, currentPageSourceUrl)) ===
	stringifyConfig(toAiManagedObjectConfigInput(right, currentPageSourceUrl));

export const hasPersistedConfigurationChanged = (
	previousConfig: AiManagedObjectConfig | null | undefined,
	nextConfig: AiManagedObjectConfigCreateInput,
): boolean => {
	if (!previousConfig) {
		return true;
	}

	return stringifyConfig(previousConfig) !== stringifyConfig(nextConfig);
};

export const hasExecutableConfigurationChanged = (
	previousConfig: AiManagedObjectConfig | null | undefined,
	nextConfig: AiManagedObjectConfigCreateInput,
): boolean => {
	if (!previousConfig) {
		return true;
	}

	const previousExecutableConfig = normalizeConfig(previousConfig);
	const nextExecutableConfig = normalizeConfig(nextConfig);
	delete previousExecutableConfig.desiredUpdateFrequency;
	delete nextExecutableConfig.desiredUpdateFrequency;

	return stableStringify(previousExecutableConfig) !== stableStringify(nextExecutableConfig);
};
